import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ListingSyncProApiService as ListingSyncProApiServiceSDK } from '@vendasta/listing-sync-pro';
import { FieldMask, ListingApiService, UpdateListingRequest } from '@vendasta/reputation';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  AGIDTOKEN,
  AppleSourceID,
  BingSourceID,
  DirectSyncErrorCode,
  FacebookSourceID,
  GoogleSourceID,
  InstagramSourceID,
  SyncData,
  XSourceID,
} from '../../../local-seo';
import { ListingSyncService } from '../../listing-sync.service';
import { ListingAccuracyText } from '../columns/presence-accuracy/accuracy-text.pipe';
import { SyncInfoModalComponent } from '../sync-info-modal/sync-info-modal.component';
import { SyndicationApiService } from '@vendasta/listing-syndication';
import { CategorySelectService } from '@galaxy/business-category';
import { LanguageService } from '../../language-service.service';
import { Provider } from '@vendasta/listing-products';

@Component({
  selector: 'local-seo-listing-sidebar',
  templateUrl: './listing-sidebar.component.html',
  styleUrls: ['./listing-sidebar.component.scss'],
  standalone: false,
})
export class ListingSidebarComponent implements OnInit, OnDestroy {
  @Input() data: SyncData;

  @Output() closed: EventEmitter<void> = new EventEmitter<void>();
  @Output() upgrade: EventEmitter<void> = new EventEmitter<void>();

  accuracyText = ListingAccuracyText;
  loadingChanges$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  subscriptions: Subscription[] = [];
  selectedSSID: string;

  AppleSourceID = AppleSourceID;
  XSourceID = XSourceID;
  readonly DirectProvider = Provider.PROVIDER_DIRECT;

  constructor(
    @Inject(AGIDTOKEN) private agid$: Observable<string>,
    protected translate: TranslateService,
    private listingSyncService: ListingSyncService,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private reputationService: ListingApiService,
    private snackBar: MatSnackBar,
    private listingSyncProApiServiceSDK: ListingSyncProApiServiceSDK,
    private listingSyndicationService: SyndicationApiService,
    private categorySelectService: CategorySelectService,
    private languageService: LanguageService,
    private lspAPIService: ListingApiService,
  ) {}

  ngOnInit(): void {
    this.selectedSSID = this.data?.account?.connectionId;
  }

  markListingAccurateEvent(marklistingaccurate: boolean) {
    this.agid$
      .pipe(
        switchMap((agid) => {
          return this.lspAPIService.updateListing(
            new UpdateListingRequest({
              listingId: { accountGroupId: agid, listingId: this.data.listingId },
              fieldMask: new FieldMask({
                paths: ['isVerified'],
              }),
              isVerified: marklistingaccurate,
            }),
          );
        }),
      )
      .subscribe(() => {
        setTimeout(() => {
          this.listingSyncService.loadSyncData(this.data?.sourceId); //emit this retry after 700ms.
        }, 700);
      });
  }

  canShowInfoModal(sourceId: number): boolean {
    return [GoogleSourceID, FacebookSourceID, InstagramSourceID, XSourceID, BingSourceID].indexOf(sourceId) !== -1;
  }

  openSyncInfoModal(): void {
    this.dialog.open(SyncInfoModalComponent, {
      data: {
        source: this.data?.sourceId,
      },
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  canForceSync(data: SyncData): boolean {
    if (data.provider !== Provider.PROVIDER_DIRECT || !data.isSyncingEnabled) {
      return false;
    }
    return !!data?.errors?.find((error) => {
      return (
        error.errorCode === DirectSyncErrorCode.UNABLE_TO_VALIDATE_ERROR ||
        error.errorCode === DirectSyncErrorCode.INVALID_FIELD_ERROR ||
        error.errorCode === DirectSyncErrorCode.MISMATCHED_FIELD_ERROR ||
        error.errorCode === DirectSyncErrorCode.UNABLE_TO_GEOCODE_ERROR ||
        error.errorCode === DirectSyncErrorCode.INVALID_CATEGORY_ERROR
      );
    });
  }
}
